import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { BulkImportIllustration } from '@lyearn/icons';
import { Box, LinkButton, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';

import { useImportJobSampleCsvRowsQuery } from '../../graphql/queries/generated/importJobSampleCsvRows';
import downloadCsvFile from '../../utils/downloadCsvFile';
import { BulkImportDialogProps } from './types';

const BulkImportDialog: React.FC<BulkImportDialogProps> = (props) => {
  const {
    onClose,
    description,
    title,
    jobPurpose,
    templateFileName,
    onSubmit,
    fileType = 'csv',
  } = props;
  const { t } = useTranslation();

  const [{ data }] = useImportJobSampleCsvRowsQuery({
    variables: { input: { purpose: jobPurpose } },
    requestPolicy: 'network-only',
  });

  const onUploadCsv = useCallback(() => {
    onSubmit?.();
    onClose();
  }, [onClose, onSubmit]);

  const isDataAvaliableToDownload = useMemo(() => {
    return !!data?.importJobSampleCsvRows?.rows;
  }, [data?.importJobSampleCsvRows?.rows]);

  const downloadTemplate = useCallback(() => {
    const rows = data?.importJobSampleCsvRows.rows;
    if (rows) {
      downloadCsvFile(rows, templateFileName);
    } else {
      console.warn('File is not ready to download');
    }
  }, [data?.importJobSampleCsvRows.rows, templateFileName]);

  return (
    <Modal
      open
      action={{
        text:
          fileType === 'csv' ? t('Common:BulkImport.UploadCsv') : t('Common:BulkImport.UploadXlsx'),
        onSubmit: onUploadCsv,
      }}
      showCloseIcon={false}
      onClose={onClose}>
      <Box className="flex flex-1">
        <BulkImportIllustration
          className="w-full flex-shrink-0"
          fill="none"
          preserveAspectRatio="xMidYMin slice"
        />
      </Box>
      <Stack className="mt-32 px-32" direction="col">
        <Typography color="text-text-primary" variant="heading-m">
          {title}
        </Typography>
        <Typography className="mt-8" color="text-text-secondary" variant="body-short-01">
          {description}
          {/* <Link appearance="link">
            <Typography className="-mt-2" variant="body-short-01">
              {t('Common:LearnMore')}
            </Typography>
          </Link> */}
        </Typography>
        <Stack alignItems="center" className="my-32" gap={4}>
          <LinkButton
            color="external"
            disabled={!isDataAvaliableToDownload}
            onClick={downloadTemplate}>
            <Typography variant="body-short-01">
              {t('Common:BulkImport.DownloadTemplate')}
            </Typography>
          </LinkButton>
          <Typography color="text-text-secondary" variant="body-short-01">
            {t('Common:BulkImport.ToGetStarted')}
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default BulkImportDialog;
